/**
 * INT-0026 — AI schedule import client (upload → parse job → poll → commit).
 * Mirrors scoresheetStore's shapes; nothing persists until commitSchedule.
 */
import create from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { axios } from './axios';

const baseUrl = `${process.env.API_URL}/api/v1`;

const createAuthHeader = (token: string) => ({
  headers: { Authorization: `Bearer ${token}` },
});

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const useScheduleImportStore = create(
  immer(() => ({
    // Upload one schedule file (image/PDF/CSV/XLSX) → { url, mimeType, fileName }.
    uploadSchedule: async (file: File, divisionId: string, token: string) => {
      const form = new FormData();
      form.append('upload', file);
      const { data } = await axios.post(
        `${baseUrl}/scheduleImports/${divisionId}/upload`,
        form,
        createAuthHeader(token),
      );
      const result = data?.result;
      if (!result?.url) throw new Error(data?.error || 'Upload failed');
      return result;
    },

    // Enqueue the AI parse job → { jobId }.
    parseSchedule: async (divisionId: string, input: { fileUrl: string; mimeType: string; fileName?: string }, token: string) => {
      const { data } = await axios.post(
        `${baseUrl}/scheduleImports/${divisionId}/parse`,
        input,
        createAuthHeader(token),
      );
      if (!data?.jobId) throw new Error(data?.error || 'Could not start the schedule parse');
      return data;
    },

    // Poll until the parse completes → the draft { teams, games, otherDivisionRows, notes }.
    pollScheduleJob: async (jobId: string, token: string, opts: { intervalMs?: number; timeoutMs?: number; onState?: (state: string, progress?: any) => void } = {}) => {
      const { intervalMs = 1500, timeoutMs = 180000, onState } = opts;
      const start = Date.now();
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { data } = await axios.get(
          `${baseUrl}/jobs/${jobId}?queue=schedules`,
          createAuthHeader(token),
        );
        const status = data?.result || data;
        if (onState) onState(status.state, status.progress);
        if (status.state === 'completed') return status.result;
        if (status.state === 'failed') throw new Error(status.error || 'Schedule parse failed');
        if (Date.now() - start > timeoutMs) throw new Error('Schedule parse timed out');
        await sleep(intervalMs);
      }
    },

    // INT-0051 — the create wizard's whole-competition import. Org-scoped
    // upload (same handler as the division upload), and ONE parse job that
    // groups rows by division label. Pasted text goes inline ({ text }); the
    // commit is onboardingStore.importSchedule.
    uploadOrgSchedule: async (file: File, orgId: string, token: string) => {
      const form = new FormData();
      form.append('upload', file);
      const { data } = await axios.post(
        `${baseUrl}/scheduleImports/org/${orgId}/upload`,
        form,
        createAuthHeader(token),
      );
      const result = data?.result;
      if (!result?.url) throw new Error(data?.error || 'Upload failed');
      return result;
    },

    parseOrgSchedule: async (
      orgId: string,
      input: { fileUrl: string; mimeType: string; fileName?: string } | { text: string },
      token: string,
    ) => {
      const { data } = await axios.post(
        `${baseUrl}/scheduleImports/org/${orgId}/parse`,
        input,
        createAuthHeader(token),
      );
      if (!data?.jobId) throw new Error(data?.error || 'Could not start the schedule parse');
      return data;
    },

    // The ONLY write: create teams + games after the organizer confirmed the
    // preview → { createdTeams, reusedTeams, createdGames, skippedDuplicates }.
    commitSchedule: async (divisionId: string, body: any, token: string) => {
      const { data } = await axios.post(
        `${baseUrl}/scheduleImports/${divisionId}/commit`,
        body,
        createAuthHeader(token),
      );
      if (data?.error) throw new Error(data.error);
      return data?.result;
    },
  })),
);

export default useScheduleImportStore;
